import { ProductCategory, ProductPrefixCategory } from "@utils/constants";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { store } from "@redux/store";
import { getVanityUrls } from "@redux/slices/globalSlice";

export type VanitySlugConfig = {
  productCategory: ProductCategory;
  sectionName?: SearchFilterTypeEnum;
  payload: Record<string, unknown>;
  title?: string;
  priceMode?: number;
  aliases?: string[];
};

export type ResolvedVanity = {
  canonicalSlug: string;
  productPrefix: string;
  productType: ProductCategory;
  priceMode?: number;
  sectionName?: SearchFilterTypeEnum;
  savedSearch: string;
  title?: string;
};

const PRODUCT_PREFIX_BY_CATEGORY: Partial<Record<ProductCategory, string>> = {
  [ProductCategory.DIAMOND]: ProductPrefixCategory.DIAMOND,
  [ProductCategory.LAB_GROWN_DIAMOND]: ProductPrefixCategory.LAB_GROWN_DIAMOND,
  [ProductCategory.GEMSTONE]: ProductPrefixCategory.GEMSTONE,
  [ProductCategory.JEWELRY]: ProductPrefixCategory.JEWELRY,
  [ProductCategory.LABGROWN_JEWELRY]: ProductPrefixCategory.LAB_GROWN_JEWELRY,
};

const getVanitySlugs = (): Record<string, VanitySlugConfig> => {
  try {
    const config = getVanityUrls(store.getState());
    return config && typeof config === "object"
      ? (config as Record<string, VanitySlugConfig>)
      : {};
  } catch {
    return {};
  }
};

const buildAliasIndex = (
  config: Record<string, VanitySlugConfig>
): Record<string, string> => {
  const index: Record<string, string> = {};
  for (const [canonicalKey, entry] of Object.entries(config)) {
    const key = canonicalKey.toLowerCase();
    if (index[key] && index[key] !== canonicalKey) {
      if (__DEV__) {
        console.warn(
          `[vanityUrls] duplicate slug "${canonicalKey}" - keeping "${index[key]}"`
        );
      }
      continue;
    }
    index[key] = canonicalKey;
    for (const alias of entry?.aliases ?? []) {
      const aliasKey = alias.toLowerCase();
      if (index[aliasKey] && index[aliasKey] !== canonicalKey) {
        if (__DEV__) {
          console.warn(
            `[vanityUrls] alias "${alias}" already maps to "${index[aliasKey]}", ignoring redefinition for "${canonicalKey}"`
          );
        }
        continue;
      }
      index[aliasKey] = canonicalKey;
    }
  }
  return index;
};

let cachedConfigRef: Record<string, VanitySlugConfig> | null = null;
let cachedAliasIndex: Record<string, string> = {};

const getAliasIndex = (
  config: Record<string, VanitySlugConfig>
): Record<string, string> => {
  if (config === cachedConfigRef) return cachedAliasIndex;
  cachedConfigRef = config;
  cachedAliasIndex = buildAliasIndex(config);
  return cachedAliasIndex;
};

const canonicalize = (value: unknown): unknown => {
  if (value === null || value === undefined) return undefined;
  if (Array.isArray(value)) {
    const items = value.map(canonicalize).filter((v) => v !== undefined);
    return items.length === 0 ? undefined : items;
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(obj).sort()) {
      const canon = canonicalize(obj[key]);
      if (canon !== undefined) out[key] = canon;
    }
    return Object.keys(out).length === 0 ? undefined : out;
  }
  if (value === "") return undefined;
  return value;
};

const stableStringify = (value: unknown): string => {
  const canon = canonicalize(value);
  return canon === undefined ? "{}" : JSON.stringify(canon);
};

const getProductPrefixSafe = (category: ProductCategory): string =>
  PRODUCT_PREFIX_BY_CATEGORY[category] ?? ProductPrefixCategory.DIAMOND;

export const resolveVanitySlug = (
  slug: string | undefined | null
): ResolvedVanity | null => {
  if (!slug) return null;
  const config = getVanitySlugs();
  const aliasIndex = getAliasIndex(config);
  const canonicalKey = aliasIndex[slug.toLowerCase()];
  if (!canonicalKey) return null;
  const entry = config[canonicalKey];
  if (!entry) return null;
  return {
    canonicalSlug: canonicalKey,
    productPrefix: getProductPrefixSafe(entry.productCategory),
    productType: entry.productCategory,
    priceMode: entry.priceMode,
    sectionName: entry.sectionName,
    savedSearch: JSON.stringify(entry.payload ?? {}),
    title: entry.title,
  };
};

export const reverseLookupVanitySlug = (
  productType: ProductCategory | string | undefined,
  payload: Record<string, unknown> | undefined,
  sectionName?: SearchFilterTypeEnum | string
): string | null => {
  if (!productType) return null;
  const config = getVanitySlugs();
  const normalizedPayload = stableStringify(payload ?? {});
  const sortedKeys = Object.keys(config).sort();
  for (const slug of sortedKeys) {
    const entry = config[slug];
    if (entry.productCategory !== productType) continue;
    if (
      entry.sectionName !== undefined &&
      sectionName !== undefined &&
      entry.sectionName !== sectionName
    ) {
      continue;
    }
    if (stableStringify(entry.payload ?? {}) === normalizedPayload) {
      return slug;
    }
  }
  return null;
};
